/**
 * Data Layer - Server Utilities
 * Server-side API utilities for Next.js API routes
 */

import { NextRequest, NextResponse } from "next/server";
import { siteConfig } from "@/config/site";
import { HTTP_STATUS, HTTP_HEADERS, MIME_TYPES } from "@/constants";
import type {
  ApiResponse,
  ApiErrorResponse,
  HttpMethod,
} from "@/shared/core/types";

/**
 * Creates a success response for API routes
 */
export function createSuccessResponse<T>(
  data: T,
  message = "common.operationSuccessful"
): NextResponse<ApiResponse<T>> {
  return NextResponse.json({
    success: true,
    message,
    response: data,
  });
}

/**
 * Creates an error response for API routes
 */
export function createErrorResponse(
  message: string,
  status = HTTP_STATUS.BAD_REQUEST,
  code?: string,
  responseData: { message: string } | null = null
): NextResponse<ApiErrorResponse> {
  return NextResponse.json(
    {
      success: false,
      message,
      code,
      response: responseData,
    } as ApiErrorResponse,
    { status }
  );
}

/**
 * Server-side API client for use in API routes
 * Enables consistent request/response handling between client and server code
 */
export const serverApiClient = {
  /**
   * Get the base URL for API requests
   */
  getBaseUrl(): string {
    return `${siteConfig.apiBaseUrl}/${siteConfig.apiMainPath}/${siteConfig.apiVersion}`;
  },

  /**
   * Makes a request to the external API
   */
  async request<T>(
    url: string,
    method: HttpMethod,
    body?: unknown,
    headers?: HeadersInit
  ): Promise<{ data: ApiResponse<T>; response: Response }> {
    const baseUrl = this.getBaseUrl();
    const fullUrl = url.startsWith("http")
      ? url
      : `${baseUrl}${url.startsWith("/") ? url : `/${url}`}`;

    try {
      const response = await fetch(fullUrl, {
        method,
        headers: {
          [HTTP_HEADERS.CONTENT_TYPE]: MIME_TYPES.JSON,
          [HTTP_HEADERS.ACCEPT]: MIME_TYPES.JSON,
          ...headers,
        },
        body: body ? JSON.stringify(body) : undefined,
      });

      const data = (await response.json()) as ApiResponse<T>;
      return { data, response };
    } catch (error) {
      console.error(
        `[Server API Client] Error calling ${method} ${url}:`,
        error
      );
      throw error;
    }
  },

  /**
   * Process a client request and forward it to the external API
   */
  async handleRequest<T>(
    request: NextRequest | undefined,
    endpoint: string,
    method: HttpMethod.GET,
    options: {
      transformRequestBody?: (body: unknown) => unknown;
      transformResponseBody?: (data: ApiResponse<T>) => unknown;
    } = {}
  ): Promise<NextResponse> {
    try {
      // Get and potentially transform request body
      let requestBody = undefined;
      if (method !== "GET" && method !== "DELETE") {
        try {
          const body = await request?.json();
          requestBody = request
            ? options.transformRequestBody
              ? options.transformRequestBody(body)
              : body
            : null;
        } catch {
          console.error("Error parsing request body");
          return createErrorResponse(
            "errors.request.invalidBody",
            HTTP_STATUS.BAD_REQUEST
          );
        }
      }

      // Forward request to external API
      const { data, response } = await this.request<T>(
        endpoint,
        method,
        requestBody,
        {
          // Forward authorization header if present
          ...(request?.headers.get(HTTP_HEADERS.AUTHORIZATION)
            ? {
                [HTTP_HEADERS.AUTHORIZATION]: request.headers.get(
                  HTTP_HEADERS.AUTHORIZATION
                )!,
              }
            : {}),
        }
      );

      // Handle API error responses
      if (!response.ok || (data && data.success === false)) {
        return createErrorResponse(
          data.message || "errors.request.processingError",
          response.status,
          data.code,
          data.response as { message: string } | null
        );
      }

      // Transform and return successful response
      const responseBody = options.transformResponseBody
        ? options.transformResponseBody(data)
        : data;
      return NextResponse.json(responseBody);
    } catch (error) {
      console.error("[API Route Handler] Unhandled error:", error);
      return createErrorResponse(
        "errors.unexpected",
        HTTP_STATUS.INTERNAL_SERVER_ERROR
      );
    }
  },
};

export default serverApiClient;
